import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, FileText, Plus, ExternalLink } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";
import { computeAcademicYear, academicYearOptions } from "@/lib/academicYear";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const CURRENT_AY = computeAcademicYear();
const AY_OPTIONS = academicYearOptions({ past: 5, future: 1 });

const emptyForm = { id: "", name: "", pdf_url: "", pdf_public_id: "", academic_year: CURRENT_AY, order: 0, status: "Active" };

export default function Academics() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/academics");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openAdd = () => {
    setForm({ ...emptyForm, order: items.length });
    setOpen(true);
  };

  const openEdit = (m) => {
    setForm({ ...emptyForm, ...m });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Document name is required");
      return;
    }
    if (!form.pdf_url) {
      toast.error("Please upload a PDF file");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        pdf_url: form.pdf_url,
        pdf_public_id: form.pdf_public_id,
        academic_year: form.academic_year,
        order: Number(form.order) || 0,
        status: form.status,
      };
      if (form.id) {
        const { data } = await api.put(`/institute/academics/${form.id}`, payload);
        setItems((arr) => arr.map((it) => (it.id === data.id ? data : it)));
        toast.success("Document updated");
      } else {
        const { data } = await api.post("/institute/academics", payload);
        setItems((arr) => [...arr, data]);
        toast.success("Document added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const id = confirmDelete?.id;
    setConfirmDelete(null);
    if (!id) return;
    try {
      await api.delete(`/institute/academics/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success("Document removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)),
    [items],
  );

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="academics-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Academics</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Academic Documents</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Upload academic PDFs — syllabi, time-tables, regulations, calendars. Files up to <strong>10 MB</strong>.
            Visitors will see a downloadable list on the public website at <em>/{"<slug>"}/academics</em>.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openAdd}
            data-testid="academics-add"
            className="inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1a3527] text-white text-sm px-4 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={2} /> Add Document
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center" data-testid="academics-empty">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#efece6] flex items-center justify-center text-[#234333]">
            <FileText size={22} strokeWidth={1.5} />
          </div>
          <div className="mt-3 text-[#6b655b]">No academic documents yet.</div>
          {canEdit && (
            <button
              onClick={openAdd}
              className="mt-4 inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1a3527] text-white text-sm px-4 py-2 rounded-sm"
            >
              <Plus size={14} strokeWidth={2} /> Upload the first document
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden" data-testid="academics-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f9f8f6] text-left text-[#6b655b] text-[11px] uppercase tracking-wider">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Document Name</th>
                <th className="px-4 py-3">Academic Year</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">PDF</th>
                {canEdit && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e0d8]">
              {sorted.map((m, idx) => (
                <tr key={m.id} data-testid={`academics-row-${m.id}`} className="hover:bg-[#f9f8f6]">
                  <td className="px-4 py-3 text-[#6b655b]">{m.order ?? idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-[#1c1a17]">{m.name}</td>
                  <td className="px-4 py-3 text-[#6b655b] font-mono text-xs">{m.academic_year || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${m.status === "Active" ? "bg-[#234333]/10 text-[#234333]" : "bg-[#6b655b]/10 text-[#6b655b]"}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {m.pdf_url ? (
                      <a
                        href={m.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#234333] hover:underline inline-flex items-center gap-1"
                        data-testid={`academics-pdf-${m.id}`}
                      >
                        <FileText size={12} strokeWidth={1.5} /> Open PDF <ExternalLink size={10} strokeWidth={1.5} />
                      </a>
                    ) : (
                      <span className="text-[#6b655b] italic">no file</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEdit(m)}
                        data-testid={`academics-edit-${m.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                      >
                        <Pencil size={12} strokeWidth={1.5} /> Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(m)}
                        data-testid={`academics-delete-${m.id}`}
                        className="ml-1 inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={12} strokeWidth={1.5} /> Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#f9f8f6] max-w-xl" data-testid="academics-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {form.id ? "Edit Document" : "Add Academic Document"}
            </DialogTitle>
            <DialogDescription className="text-[#6b655b]">
              PDF files only — up to 10 MB. The document will be downloadable from the public Academics page.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Name of the Document *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className={inputCls}
                placeholder="e.g. B.Tech Computer Engineering Syllabus 2026-27"
                data-testid="academics-input-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Academic Year *</label>
              <select
                value={form.academic_year}
                onChange={(e) => setForm((f) => ({ ...f, academic_year: e.target.value }))}
                className={inputCls}
                data-testid="academics-input-academic-year"
              >
                {AY_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                    {y === CURRENT_AY ? " (current)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Display Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                className={inputCls}
                data-testid="academics-input-order"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className={inputCls}
                data-testid="academics-input-status"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">PDF File (≤ 10 MB) *</label>
              <FileUploader
                kind="inst_academic_pdf"
                label="Upload PDF"
                url={form.pdf_url}
                testid="academics-pdf-input"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, pdf_url: url, pdf_public_id: public_id }))
                }
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <button
              onClick={() => setOpen(false)}
              className="text-sm text-[#6b655b] hover:bg-[#efece6] px-4 py-2 rounded-sm"
              data-testid="academics-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="academics-save"
              className="bg-[#234333] hover:bg-[#1a3527] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : form.id ? "Save Changes" : "Add Document"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#f9f8f6]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">Remove document?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              &quot;{confirmDelete?.name}&quot; will be removed from the public website.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white border-[#e5e0d8]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="academics-confirm-delete"
              className="bg-[#9e2a2b] hover:bg-[#7a1f20] text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
